import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, forkJoin, take } from 'rxjs';
import { debounceTime, filter, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { BaseCalendarModel } from '../../core/models/calendar/base-calendar.model';
import { ShiveNotificationService } from '../../core/services/controls/shive-notification.service';
import { CronJobsService } from '../../core/services/cron-jobs.service';
import { SharedTrackerHandlerService } from '../../core/services/features/time-tracking/shared-tracker-handler.service';
import { selectHolidayDates } from '../../core/state/settings/holiday-templates/holiday-templates.selectors';
import Autobind from '../../shared/typescript-decorators/autobind.decorator';
import { CalendarEvents, CalendarViews } from './calendar-types';
import { calendarCronjobsInterval } from './calendar.config';
import { ActiveTimeBlockTimerService } from './components/time-block/active/active-time-block-timer.service';
import { ActiveTimeBlockTrackerService } from './components/time-block/active/active-time-block-tracker.service';
import { NewTimeBlockGenerationService } from './components/time-block/generation/new-time-block-generation.service';
import { TimeBlockHttpCallbackService } from './components/time-block/http/time-block-http-callback.service';
import { InteractHandlerService } from './components/time-block/interaction/movement/interact-handler.service';
import { CalendarScrollbarService } from './services/calendar-scrollbar.service';
import { CalendarServiceHelper } from './services/calendar-service-helper';
import { CalendarService } from './services/calendar.service';
import { TimeCoordinateMappingService } from './time-mapping/time-coordinate-mapping.service';
import { WINDOW } from './util/browser-window-ref';
import { NowIndicatorCSSId } from './util/calendar-style.constants';
import { DateTimeHelper } from './util/date-time-helper';
import { TimeBlockContainerDirective } from './views/time-block-container.directive';

@Component({
  selector: 'app-shive-calendar',
  templateUrl: './calendar.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarComponent implements OnInit, OnDestroy {
  // Signals
  finishedLoading = signal<boolean>(false);

  public CalendarView = CalendarViews;
  public calendarViewMode: CalendarViews;
  public fixIntervalLanesLoaded$ = new BehaviorSubject<
    [QueryList<TimeBlockContainerDirective>, QueryList<ElementRef>]
  >(null);
  private readonly subs = new SubSink();
  private initialDate: Date;
  private dayChangeJob: string;

  // DI
  private readonly window = inject<Window>(WINDOW);

  constructor(
    public calendarService: CalendarService,
    private readonly newTimeBlockGenerationService: NewTimeBlockGenerationService,
    private readonly notificationService: ShiveNotificationService,
    private readonly interactHandlerService: InteractHandlerService,
    private readonly store: Store,
    private readonly activeTimeBlockTrackerService: ActiveTimeBlockTrackerService,
    private readonly activeTimeBlockTimerService: ActiveTimeBlockTimerService,
    private readonly timeCoordinateMapperService: TimeCoordinateMappingService,
    private readonly timeBlockHttpCallbackService: TimeBlockHttpCallbackService,
    private readonly calendarScrollbarService: CalendarScrollbarService,
    private readonly calendarCronJobsService: CronJobsService,
    private readonly sharedTrackerHandlerService: SharedTrackerHandlerService,
  ) {}

  ngOnInit(): void {
    this.timeBlockHttpCallbackService.handleHTTPCallbacks();
    this.initEvents();
    this.initOvernightInterval();

    // Load all calendar specific data (e.g. calendar model, holiday templates).
    const data$ = this.calendarService.initUserRelatedCalendarData().data$.pipe(take(1));
    const calendarModel$ = this.calendarService.initConfigData();

    forkJoin([calendarModel$, data$])
      .pipe(
        take(1),
        switchMap(() => {
          return this.store.select(selectHolidayDates());
        }),
      )
      .subscribe((holidays) => {
        const calendarEvents = [CalendarEvents.Start];
        const today = new Date();
        const calendarModel = this.calendarService.model;
        calendarModel.holidays = holidays;
        this.calendarService.updateCalendarData(today, calendarEvents, calendarModel);
        this.calendarViewMode = this.calendarService.model.calendarViewMode;
        this.finishedLoading.set(true);
      });
  }

  ngOnDestroy(): void {
    this.interactHandlerService.deregisterHandlers();
    this.calendarScrollbarService.destroyScrollbars();

    // Stop all calendar related jobs
    this.calendarCronJobsService.deregisterJob(this.dayChangeJob);
    this.activeTimeBlockTrackerService.deregisterTimeTracking();
    this.activeTimeBlockTimerService.deregisterTimeTracking();

    // Unsubscribe
    this.subs.unsubscribe();
    this.calendarService.calendarDestroyed$.next(null);
  }

  private initEvents(): void {
    const targetEvents = [
      CalendarEvents.SwitchedCalendarViewMode,
      CalendarEvents.InitializedCalendarView,
      CalendarEvents.ChangedStartEndDates,
      CalendarEvents.RenderedTimeBlocks,
    ];
    const callback = (calendarModel: BaseCalendarModel, events: CalendarEvents[]): void => {
      if (events.includes(CalendarEvents.RenderedTimeBlocks)) {
        this.scrollToNowIndicator();
        this.initTimeTracking();
      } else {
        if (calendarModel.calendarViewMode === CalendarViews.MonthGrid) {
          this.calendarScrollbarService.disableScrollbars(true);
        } else {
          this.calendarScrollbarService.calendarHeaderScrollbar.disable(true);
          this.calendarScrollbarService.calendarBodyScrollbar.disable(false);
        }
        this.calendarViewMode = calendarModel.calendarViewMode;
        this.notificationService.hide();
        this.interactHandlerService.deregisterHandlers();
      }

      // We need to wait until scrolling has been finished. There is no scrollend event for the smooth scrollbar, unfortunately.
      // So we wait a bit until scrolling should be finished.
      this.window.setTimeout(() => {
        this.newTimeBlockGenerationService.readyForGeneration = true;
      }, 500);
    };

    this.subs.sink = CalendarServiceHelper.calendarModelUpdated(
      this.calendarService,
      callback,
      targetEvents,
    );
  }

  private scrollToNowIndicator(): void {
    this.subs.sink = this.calendarScrollbarService.calendarBodyScrollbarInitialized$
      .pipe(
        filter((scrollbar) => scrollbar !== null),
        take(1),
      )
      .subscribe(() => {
        const firstVisibleDay = this.calendarService.model.calendarProperties.visibleStartDate;
        const weekStartsOn = this.calendarService.model.calendarProperties.weekStartsOn;
        if (
          !document.querySelector(NowIndicatorCSSId) ||
          !DateTimeHelper.isSameWeek(firstVisibleDay, new Date(), { weekStartsOn })
        ) {
          return;
        }

        const calendarBodyHeight =
          this.calendarService.model.geometryData.calendarBodyVisibleHeight;
        this.calendarScrollbarService.calendarBodyScrollbar.scrollIntoView(
          document.querySelector(NowIndicatorCSSId),
          {
            alignToTop: false,
            onlyScrollIfNeeded: false,
            offsetBottom: calendarBodyHeight / 2,
          },
        );
      });
  }

  private initOvernightInterval() {
    this.initialDate = new Date();
    this.dayChangeJob = this.calendarCronJobsService.registerJob(
      this.checkForDayChange,
      calendarCronjobsInterval,
    );
  }

  /**
   * Check if the day changed (i.e. now indicator passed midnight).
   */
  @Autobind
  private checkForDayChange(): void {
    this.subs.sink = this.timeCoordinateMapperService.pixelsPerMinuteCalculated$
      .pipe(
        filter((ppm) => !!ppm),
        debounceTime(10), // For the very rare case that a user refreshes the page at exactly 00:00.
      )
      .subscribe(() => {
        const now = new Date();
        if (!DateTimeHelper.isSameDay(this.initialDate, now)) {
          this.initialDate = now;
          const calendarEvents = [CalendarEvents.ChangedStartEndDates];
          this.calendarService.updateCalendarData(now, calendarEvents, this.calendarService.model);
        }
      });
  }

  private initTimeTracking() {
    this.activeTimeBlockTrackerService.registerTimeTracking();
    this.activeTimeBlockTimerService.registerTimeTracking();

    this.subs.sink = this.sharedTrackerHandlerService.trackerResponse$
      .pipe(
        filter(
          (response) =>
            response !== null &&
            response.isTrackerAlive !== this.calendarService.model.shiveTrackerActive,
        ),
      )
      .subscribe((trackerResponse) => {
        this.activeTimeBlockTrackerService.toggleTrackerAlive(trackerResponse.isTrackerAlive);
      });
  }
}
